import { isSameDay, parseISO, differenceInHours } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import type { DayTimeSlots, TimeSlot } from '../components/calendar/types'
import type { TimeSlotResponse, WeekTimeSlotsResponse } from '../api/ester-b2b-api/private/timeSlots/types'
import { applyTimeZonetoWebdocDate, formatDateForAPI, getDaysInWeek } from './dates'

/**
 * Check if a specific time slot is selected
 */
export const isSlotSelected = (slot: TimeSlot, date: Date, selectedSlot?: { slotId: string; date: Date }): boolean => {
  if (!selectedSlot) return false
  return slot.id === selectedSlot.slotId && isSameDay(date, selectedSlot.date)
}

/**
 * Check if a meeting time is within the next 24 hours
 */
export const isWithin24Hours = (appointment: { date: string; startTime: string }): boolean => {
  const appointmentDateTime = applyTimeZonetoWebdocDate(appointment.date, appointment.startTime)
  const now = new Date()
  const hoursUntilAppointment = differenceInHours(appointmentDateTime, now)
  return hoursUntilAppointment < 24 && hoursUntilAppointment >= 0
}

/**
 *
 * TIMEZONE HANDLING:
 * - Backend returns times in Swedish timezone (Europe/Stockholm)
 * - Uses fromZonedTime() to properly convert Swedish time to UTC
 * - Display automatically adapts to user's browser timezone
 * - Example: 9 AM Swedish time shows as 3 AM for NYC user, 5 PM for Tokyo user
 *
 * Optimized to group slots by date once instead of filtering for each day (O(n+m) vs O(n*m))
 */
export const transformWeekTimeSlotsResponse = (response: WeekTimeSlotsResponse): DayTimeSlots[] => {
  const weekStart = parseISO(response.weekStart)
  const days = getDaysInWeek(weekStart)

  const slotsByDateKey = new Map<string, TimeSlotResponse[]>()

  response.slots.forEach((slot) => {
    const dateKey = slot.startTime.split('T')[0] || '' // Extract YYYY-MM-DD
    if (dateKey && !slotsByDateKey.has(dateKey)) {
      slotsByDateKey.set(dateKey, [])
    }
    if (dateKey) {
      slotsByDateKey.get(dateKey)!.push(slot)
    }
  })

  return days.map((date) => {
    const dateKey = formatDateForAPI(date)
    const daySlots = slotsByDateKey.get(dateKey) || []

    const slots: TimeSlot[] = daySlots.map((slot: TimeSlotResponse) => ({
      id: slot.id,
      startTime: fromZonedTime(parseISO(slot.startTime), 'Europe/Stockholm'),
      endTime: fromZonedTime(parseISO(slot.endTime), 'Europe/Stockholm'),
      specialistId: slot.specialistId,
    }))

    // Sort slots by start time
    slots.sort((a, b) => a.startTime.getTime() - b.startTime.getTime())

    return {
      date,
      slots,
      // Backend only returns available slots, so day is fully booked if no slots returned
      isFullyBooked: slots.length === 0,
    }
  })
}

export const translateLanguagesToEn = (languages: string[]): string[] => {
  const languageMap: { [key: string]: string } = {
    Arabiska: 'Arabic',
    Assyriska: 'Assyrian',
    Bengali: 'Bengali',
    Danska: 'Danish',
    Dari: 'Dari',
    Engelska: 'English',
    Farsi: 'Farsi',
    Finska: 'Finnish',
    Franska: 'French',
    Grekiska: 'Greek',
    Hindi: 'Hindi',
    Indonesiska: 'Indonesian',
    Isländska: 'Icelandic',
    Italienska: 'Italian',
    Japanska: 'Japanese',
    Kantonesiska: 'Cantonese',
    Kurdiska: 'Kurdish',
    Mandarin: 'Mandarin',
    Norska: 'Norwegian',
    Persiska: 'Persian',
    Polska: 'Polish',
    Portugisiska: 'Portuguese',
    Rumänska: 'Romanian',
    Ryska: 'Russian',
    Samiska: 'Sami',
    Somaliska: 'Somali',
    Spanska: 'Spanish',
    Svenska: 'Swedish',
    Syrianska: 'Syriac',
    Turkiska: 'Turkish',
    Tyska: 'German',
    Ukrainska: 'Ukrainian',
    Ungerska: 'Hungarian',
    Urdu: 'Urdu',
    Vietnamesiska: 'Vietnamese',
  }

  return languages.map((lang) => languageMap[lang] || lang)
}

export const getLanguageNames = (languages: string[], locale: string): string => {
  if (languages && locale === 'sv') {
    return languages.join(', ')
  } else {
    const translatedLanguages = translateLanguagesToEn(languages)
    return translatedLanguages.join(', ')
  }
}
